<template>
  <div
    :id="rootId"
    ref="root"
    class="qe-root relative rounded-lg border bg-white dark:bg-[#1C2128] transition-colors"
    :class="[
      enabled
        ? 'border-gray-200 dark:border-gray-700 focus-within:border-blue-500 dark:focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
        : 'border-gray-200 dark:border-gray-700',
      enabled ? '' : 'qe-readonly',
    ]"
  >
    <!-- Quill mounts here and injects its toolbar as a sibling above it. -->
    <div ref="editor" :style="{ height: containerHeight }"></div>

    <!-- Table controls. The grid picker is anchored to the toolbar rather
         than to the button, since Quill owns the button's DOM. -->
    <template v-if="enableTable">
      <div v-if="tableMenuOpen" @click="tableMenuOpen = false" class="fixed inset-0 z-20"></div>
      <div
        v-if="tableMenuOpen"
        :style="tablePanelStyle"
        class="z-30 p-2.5 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg ring-1 ring-black/5"
      >
        <div class="flex flex-col gap-1" @mouseleave="tableHover = { rows: 0, cols: 0 }">
          <div v-for="row in TABLE_GRID_ROWS" :key="row" class="flex gap-1">
            <button
              v-for="col in TABLE_GRID_COLS"
              :key="col"
              type="button"
              class="w-5 h-5 rounded-[3px] border transition-colors"
              :class="row <= tableHover.rows && col <= tableHover.cols
                ? 'bg-blue-500 border-blue-500'
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:border-blue-400'"
              @mouseenter="tableHover = { rows: row, cols: col }"
              @click="insertTable(row, col)"
              :aria-label="`Insert ${row} by ${col} table`"
            ></button>
          </div>
        </div>
        <p class="mt-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
          {{ tableHover.rows ? `${tableHover.rows} x ${tableHover.cols}` : 'Pick a size' }}
        </p>
      </div>

      <!-- Row and column actions, shown only while the caret is inside a
           table. Quill's commands act on the caret position, so offering
           them anywhere else would silently do nothing. -->
      <div
        v-if="inTable"
        class="flex flex-wrap items-center gap-1 px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-white/[0.03]"
      >
        <span class="text-xs font-medium text-gray-400 dark:text-gray-500 mr-1">Table</span>
        <button v-for="action in TABLE_ACTIONS" :key="action.command"
          type="button"
          :title="action.label"
          @click="runTableCommand(action.command)"
          class="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-all duration-150"
        >{{ action.label }}</button>
        <button
          type="button"
          title="Delete table"
          @click="runTableCommand('deleteTable')"
          class="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-[#1C2128] border border-red-200 dark:border-red-900/50 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition-all duration-150 ml-auto"
        >Delete table</button>
      </div>
    </template>

    <!-- Image upload affordances. Only rendered when the parent opted in by
         passing an uploadImage function. -->
    <template v-if="uploadImage">
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="onFileChosen"
      />

      <div
        v-if="uploading || uploadFailed"
        class="flex items-center gap-2 px-3 py-2 border-t text-xs font-medium"
        :class="uploadFailed
          ? 'border-red-100 dark:border-red-900/40 bg-red-50 dark:bg-red-900/15 text-red-600 dark:text-red-300'
          : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400'"
      >
        <svg v-if="uploading" class="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <svg v-else class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
        <span>{{ uploading ? 'Uploading image' : 'That image could not be uploaded. Please try again.' }}</span>
      </div>
    </template>

    <!-- Kept inside the root so the component stays single rooted and its
         fallthrough attrs still land; the bubble teleports to body anyway. -->
    <TooltipBubble
      :visible="toolbarTooltip.visible.value"
      :text="toolbarTooltipText"
      :bubble-style="toolbarTooltip.bubbleStyle.value"
      :arrow-style="toolbarTooltip.arrowStyle.value"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, useId } from 'vue'
import Quill from 'quill'
import { attachImageResize } from './quillImageResize.js'
import { useTooltip } from 'composables/useTooltip.js'
import TooltipBubble from 'controllers/vue_components/shared/TooltipBubble.vue'

defineOptions({ name: 'QuillEditor', inheritAttrs: true })

const props = defineProps({
  // Kept untyped on purpose: the existing consumers pass through values that
  // can briefly be undefined while their own data loads, and Vue's Boolean
  // casting would change what they used to see.
  content: { default: '' },
  enable: { default: false },
  opts: { type: Object, default: () => ({}) },
  // Opt-in image uploads. Takes the raw File, resolves to a URL string on
  // success or null on failure, never rejects. Absent means no image button.
  uploadImage: { type: Function, default: null },
  // Opt-in tables. Quill 2 ships a basic table module (insert, add or remove
  // rows and columns) with no toolbar UI of its own, so the button and the
  // size picker below are ours.
  enableTable: { type: Boolean, default: false },
})

const emit = defineEmits(['update-content'])

// Vue hands every instance its own id, so two editors on one page no longer
// collide the way the old hardcoded id="editor" did.
const rootId = `quill-editor-${useId()}`

const root = ref(null)
const editor = ref(null)
const fileInput = ref(null)

const uploading = ref(false)
const uploadFailed = ref(false)

let quill = null
let savedRange = null
let detachImageResize = null

const enabled = computed(() => !!props.enable)
const containerHeight = computed(() => (props.opts && props.opts.height) || '250px')

/* ------------------------------------------------------------------ *
 * Toolbar
 * ------------------------------------------------------------------ */

// A writing toolbar, not a kitchen sink. Grouped so the CSS can draw a
// separator between groups, and so it wraps into tidy rows when narrow.
function toolbarGroups() {
  // `.filter` drops the groups that opted out, so a disabled feature leaves
  // no empty separator behind in the toolbar.
  return [
    [{ header: [1, 2, 3, 4, 5, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }, { indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    props.uploadImage ? ['link', 'image'] : ['link'],
    props.enableTable ? ['table'] : [],
    ['clean'],
  ].filter((group) => group.length)
}

const BUTTON_TITLES = [
  ['button.ql-bold', 'Bold'],
  ['button.ql-italic', 'Italic'],
  ['button.ql-underline', 'Underline'],
  ['button.ql-strike', 'Strikethrough'],
  ['button.ql-blockquote', 'Quote'],
  ['button.ql-code', 'Inline code'],
  ['button.ql-code-block', 'Code block'],
  ['button.ql-list[value="ordered"]', 'Numbered list'],
  ['button.ql-list[value="bullet"]', 'Bulleted list'],
  ['button.ql-list[value="check"]', 'Checklist'],
  ['button.ql-indent[value="-1"]', 'Decrease indent'],
  ['button.ql-indent[value="+1"]', 'Increase indent'],
  ['button.ql-link', 'Insert link'],
  ['button.ql-image', 'Insert image'],
  ['button.ql-clean', 'Clear formatting'],
  ['button.ql-table', 'Insert table'],
  ['.ql-header .ql-picker-label', 'Heading style'],
  ['.ql-align .ql-picker-label', 'Text alignment'],
]

const ALIGN_ITEM_TITLES = {
  '': 'Align left',
  center: 'Align center',
  right: 'Align right',
  justify: 'Justify',
}

// Quill 2 ships the same glyph for `code` and `code-block`, which reads as two
// identical buttons sitting next to each other. Give the block variant its own
// mark. Classes are Quill's own so its active/hover recolouring still applies.
const CODE_BLOCK_ICON = `
  <svg viewbox="0 0 18 18">
    <rect class="ql-stroke qe-thin" fill="none" x="2" y="3.5" width="14" height="11" rx="2"/>
    <line class="ql-stroke qe-thin" x1="5" x2="9" y1="7.5" y2="7.5"/>
    <line class="ql-stroke qe-thin" x1="5" x2="12" y1="11" y2="11"/>
  </svg>`

function toolbarEl() {
  const module = quill && quill.getModule('toolbar')
  return module ? module.container : null
}

// Labels a control for both the styled tooltip and assistive tech. No native
// `title`: the browser would show its own grey box a second later on top of
// ours.
function labelControl(node, title) {
  node.setAttribute('aria-label', title)
  node.setAttribute('data-qe-tooltip', title)
}

function decorateToolbar() {
  const el = toolbarEl()
  if (!el) return

  BUTTON_TITLES.forEach(([selector, title]) => {
    el.querySelectorAll(selector).forEach((node) => labelControl(node, title))
  })

  el.querySelectorAll('.ql-align .ql-picker-item').forEach((node) => {
    const title = ALIGN_ITEM_TITLES[node.getAttribute('data-value') || '']
    if (title) labelControl(node, title)
  })

  // Heading items already show their name as text, so a tooltip repeating it
  // would only get in the way. The aria-label is still needed: Quill draws
  // that text with CSS `::before`, which screen readers don't reliably read.
  el.querySelectorAll('.ql-header .ql-picker-item').forEach((node) => {
    const value = node.getAttribute('data-value')
    node.setAttribute('aria-label', value ? `Heading ${value}` : 'Normal text')
  })

  const codeBlock = el.querySelector('button.ql-code-block')
  if (codeBlock) codeBlock.innerHTML = CODE_BLOCK_ICON
}

// Quill owns the toolbar's DOM, so its buttons can't be wrapped in
// `<Tooltip>`. One delegated set of listeners on the toolbar drives a single
// shared bubble instead, pointed at whichever labelled control is under the
// cursor (or keyboard focus).
const toolbarTooltip = useTooltip()
const toolbarTooltipText = ref('')
let toolbarTooltipTrigger = null

function onToolbarMouseOver(event) {
  const trigger = event.target.closest?.('[data-qe-tooltip]') || null
  if (trigger === toolbarTooltipTrigger) return
  toolbarTooltip.hide()
  toolbarTooltipTrigger = trigger
  if (!trigger) return
  toolbarTooltipText.value = trigger.getAttribute('data-qe-tooltip')
  toolbarTooltip.scheduleShow(trigger)
}

// Keyboard focus only. Clicking a button focuses it too, and a bubble that
// flashed up on every click would just be noise.
function onToolbarFocusIn(event) {
  const trigger = event.target.closest?.('[data-qe-tooltip]')
  if (!trigger || !trigger.matches(':focus-visible')) return
  toolbarTooltipTrigger = trigger
  toolbarTooltipText.value = trigger.getAttribute('data-qe-tooltip')
  toolbarTooltip.showNow(trigger)
}

function clearToolbarTooltip() {
  toolbarTooltipTrigger = null
  toolbarTooltip.hide()
}

// Click hides but keeps the trigger, so the bubble doesn't come straight back
// over an opened picker while the cursor is still resting on its label. On
// touch, the emulated mouseover schedules a show and this click cancels it.
const TOOLBAR_TOOLTIP_LISTENERS = {
  mouseover: onToolbarMouseOver,
  mouseleave: clearToolbarTooltip,
  focusin: onToolbarFocusIn,
  focusout: clearToolbarTooltip,
  click: toolbarTooltip.hide,
}

function applyEnabled() {
  if (!quill) return
  quill.enable(enabled.value)
  const el = toolbarEl()
  if (el) el.style.display = enabled.value ? '' : 'none'
}

/* ------------------------------------------------------------------ *
 * Image upload
 * ------------------------------------------------------------------ */

function setImageButtonBusy(busy) {
  const el = toolbarEl()
  if (!el) return
  const button = el.querySelector('button.ql-image')
  if (!button) return
  button.disabled = busy
  button.setAttribute('aria-busy', busy ? 'true' : 'false')
  button.classList.toggle('qe-busy', busy)
}

function openFilePicker() {
  if (!props.uploadImage || uploading.value) return
  // Remember where the caret was before the picker steals focus.
  savedRange = quill ? quill.getSelection(true) : null
  uploadFailed.value = false
  if (fileInput.value) {
    fileInput.value.value = ''
    fileInput.value.click()
  }
}

async function onFileChosen(event) {
  const input = event.target
  const file = input.files && input.files[0]
  input.value = ''
  if (!file || !props.uploadImage || !quill) return

  uploading.value = true
  uploadFailed.value = false
  setImageButtonBusy(true)

  let url = null
  try {
    url = await props.uploadImage(file)
  } catch {
    url = null
  } finally {
    uploading.value = false
    setImageButtonBusy(false)
  }

  // Contract: null means the upload failed. Leave the document untouched.
  if (typeof url !== 'string' || url === '') {
    uploadFailed.value = true
    return
  }

  const index = savedRange && Number.isInteger(savedRange.index)
    ? savedRange.index
    : quill.getLength()

  quill.insertEmbed(index, 'image', url, 'user')
  quill.setSelection(index + 1, 0, 'silent')
  emit('update-content', quill.root.innerHTML)
}

/* ------------------------------------------------------------------ *
 * Floating panels
 * ------------------------------------------------------------------ */

// The toolbar scrolls horizontally (`overflow-x: auto`) so it can stay a
// single row on narrow screens. CSS makes that clip *both* axes: when one
// axis is not `visible`, the other computes to `auto`. So every dropdown
// Quill renders inside the toolbar got cut off at the toolbar's own height,
// which is why the heading picker showed only a sliver of "Heading 1".
//
// Fixing it in CSS is not possible while the toolbar still scrolls, so the
// panels are lifted out of the clip instead: `position: fixed` is not
// clipped by an ancestor's overflow, and nothing here establishes a
// containing block for fixed positioning.
function pinToViewport(el, anchorRect, width) {
  const left = Math.min(
    Math.max(8, anchorRect.left),
    Math.max(8, window.innerWidth - width - 8),
  )
  el.style.position = 'fixed'
  el.style.top = `${anchorRect.bottom + 6}px`
  el.style.left = `${left}px`
}

function clearPin(el) {
  el.style.position = ''
  el.style.top = ''
  el.style.left = ''
}

// Quill toggles `.ql-expanded` itself, so this runs after its own click
// handler and simply reacts to whatever state it left behind.
function positionExpandedPickers() {
  const el = toolbarEl()
  if (!el) return

  // Only one floating panel at a time, the other half of the rule in
  // toggleTableMenu.
  if (el.querySelector('.ql-picker.ql-expanded')) tableMenuOpen.value = false

  el.querySelectorAll('.ql-picker').forEach((picker) => {
    const options = picker.querySelector('.ql-picker-options')
    if (!options) return

    if (picker.classList.contains('ql-expanded')) {
      const rect = picker.getBoundingClientRect()
      pinToViewport(options, rect, Math.max(options.offsetWidth, rect.width))
      options.style.minWidth = `${rect.width}px`
    } else {
      clearPin(options)
      options.style.minWidth = ''
    }
  })
}

// A viewport pinned panel does not move with its anchor on its own, so any
// scroll or resize either re-pins it or closes it.
function repositionOpenPanels() {
  positionExpandedPickers()
  if (tableMenuOpen.value) positionTablePanel()
}

function closeExpandedPickers() {
  toolbarEl()?.querySelectorAll('.ql-picker.ql-expanded').forEach((picker) => {
    picker.classList.remove('ql-expanded')
    const options = picker.querySelector('.ql-picker-options')
    if (options) {
      clearPin(options)
      options.style.minWidth = ''
    }
  })
}

/* ------------------------------------------------------------------ *
 * Tables
 * ------------------------------------------------------------------ */

// Quill 2's table module exposes insertTable / insertRow* / insertColumn* /
// deleteRow / deleteColumn / deleteTable but ships no UI at all, so the grid
// picker and the context row below are the whole interface.
const TABLE_GRID_ROWS = 6
const TABLE_GRID_COLS = 6

const TABLE_ACTIONS = [
  { command: 'insertRowAbove', label: 'Row above' },
  { command: 'insertRowBelow', label: 'Row below' },
  { command: 'insertColumnLeft', label: 'Column left' },
  { command: 'insertColumnRight', label: 'Column right' },
  { command: 'deleteRow', label: 'Delete row' },
  { command: 'deleteColumn', label: 'Delete column' },
]

// Matches the rendered panel: 6 cells of 20px, 5 gaps of 4px, 10px padding
// each side. Used to keep it on screen when the button sits near the right
// edge, which it does, since the table button is the last group.
const TABLE_PANEL_WIDTH = 160

const tableMenuOpen = ref(false)
const tablePanelStyle = ref({})
const tableHover = ref({ rows: 0, cols: 0 })
const inTable = ref(false)

function tableModule() {
  return quill ? quill.getModule('table') : null
}

function toggleTableMenu() {
  tableMenuOpen.value = !tableMenuOpen.value
  tableHover.value = { rows: 0, cols: 0 }
  if (!tableMenuOpen.value) return

  // Only one floating panel at a time.
  closeExpandedPickers()
  // Quill owns the toolbar button's DOM, so the panel cannot be a child of
  // it. It is positioned against the button's measured rect instead, which
  // is what puts it directly under the control that opened it rather than
  // over at the toolbar's left edge.
  nextTick(positionTablePanel)
}

function positionTablePanel() {
  const button = toolbarEl()?.querySelector('button.ql-table')
  if (!button) return

  const rect = button.getBoundingClientRect()
  const left = Math.min(
    Math.max(8, rect.left),
    Math.max(8, window.innerWidth - TABLE_PANEL_WIDTH - 8),
  )
  tablePanelStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 6}px`,
    left: `${left}px`,
    width: `${TABLE_PANEL_WIDTH}px`,
  }
}

function insertTable(rows, cols) {
  const module = tableModule()
  if (!module) return
  quill.focus()
  module.insertTable(rows, cols)
  tableMenuOpen.value = false
  emit('update-content', quill.root.innerHTML)
  refreshTableContext()
}

// Quill's row and column commands act on wherever the caret is, so the
// context row only makes sense while the selection is actually inside a
// table. Recomputed on selection changes rather than polled.
function refreshTableContext() {
  if (!props.enableTable || !quill) {
    inTable.value = false
    return
  }
  const range = quill.getSelection()
  if (!range) {
    inTable.value = false
    return
  }
  const [leaf] = quill.getLeaf(range.index)
  inTable.value = !!leaf?.domNode?.parentElement?.closest?.('table')
}

function runTableCommand(command) {
  const module = tableModule()
  if (!module || typeof module[command] !== 'function') return
  module[command]()
  emit('update-content', quill.root.innerHTML)
  refreshTableContext()
}

/* ------------------------------------------------------------------ *
 * Lifecycle
 * ------------------------------------------------------------------ */

function onTextChange() {
  emit('update-content', quill.root.innerHTML)
}

onMounted(() => {
  quill = new Quill(editor.value, {
    theme: 'snow',
    placeholder: (props.opts && props.opts.placeholder) || '',
    modules: {
      table: props.enableTable,
      toolbar: {
        container: toolbarGroups(),
        handlers: {
          ...(props.uploadImage ? { image: openFilePicker } : {}),
          ...(props.enableTable ? { table: toggleTableMenu } : {}),
        },
      },
    },
  })

  decorateToolbar()
  // Runs after Quill's own click handler, so it sees the final .ql-expanded
  // state rather than the one before the click.
  toolbarEl()?.addEventListener('click', positionExpandedPickers)
  Object.entries(TOOLBAR_TOOLTIP_LISTENERS).forEach(([type, listener]) => {
    toolbarEl()?.addEventListener(type, listener)
  })
  window.addEventListener('scroll', repositionOpenPanels, true)
  window.addEventListener('resize', repositionOpenPanels)
  quill.on('text-change', onTextChange)
  if (props.enableTable) quill.on('selection-change', refreshTableContext)
  applyEnabled()

  // Click-to-resize for embedded images. Only where images can exist at all,
  // so the other consumers of this component take on none of it.
  if (props.uploadImage) {
    detachImageResize = attachImageResize(quill, root.value, onTextChange)
  }

  quill.root.innerHTML = props.content == null ? '' : props.content
})

onBeforeUnmount(() => {
  toolbarEl()?.removeEventListener('click', positionExpandedPickers)
  Object.entries(TOOLBAR_TOOLTIP_LISTENERS).forEach(([type, listener]) => {
    toolbarEl()?.removeEventListener(type, listener)
  })
  window.removeEventListener('scroll', repositionOpenPanels, true)
  window.removeEventListener('resize', repositionOpenPanels)
  detachImageResize?.()
  detachImageResize = null
  if (quill) {
    quill.off('text-change', onTextChange)
    quill.off('selection-change', refreshTableContext)
  }
  quill = null
})

watch(
  () => props.content,
  (value) => {
    if (!quill) return
    const next = value == null ? '' : value
    if (quill.root.innerHTML !== next) quill.root.innerHTML = next
  },
)

watch(() => props.enable, applyEnabled)
</script>

<!--
  Deliberately NOT scoped. Everything below is Quill's own third-party DOM
  (`.ql-*`), which per CLAUDE.md is the one place raw CSS is correct; the
  `.qe-root` prefix keeps it contained. Selector shapes mirror Quill's snow
  theme exactly so specificity ties and this file, loaded after the Sprockets
  vendor copy of snow, wins on order. That also keeps every rule here *below*
  a page-level `.page-class :deep(.ql-*)` override, so pages such as the task
  detail page can still restyle the editor without fighting this file.

  Theme switching goes through custom properties on purpose: dark mode never
  raises the specificity of a `.ql-*` rule, so page-level overrides keep
  winning in both themes.
-->
<style>
.qe-root {
  --qe-toolbar-bg: rgb(249 250 251);
  --qe-divider: rgb(229 231 235);
  --qe-separator: rgb(209 213 219);
  --qe-icon: rgb(75 85 99);
  --qe-icon-hover: rgb(17 24 39);
  --qe-hover-bg: rgb(229 231 235);
  --qe-active: rgb(37 99 235);
  --qe-active-bg: rgb(219 234 254);
  --qe-text: rgb(31 41 55);
  --qe-muted: rgb(156 163 175);
  --qe-panel: rgb(255 255 255);
  --qe-code-bg: rgb(243 244 246);
  --qe-code-fg: rgb(31 41 55);
  --qe-quote-bar: rgb(209 213 219);
  --qe-shadow: 0 10px 24px -8px rgb(15 23 42 / 0.18);
  --qe-check-border: rgb(156 163 175);
}

.dark .qe-root {
  --qe-toolbar-bg: rgb(255 255 255 / 0.03);
  --qe-divider: rgb(55 65 81);
  --qe-separator: rgb(75 85 99);
  --qe-icon: rgb(156 163 175);
  --qe-icon-hover: rgb(243 244 246);
  --qe-hover-bg: rgb(255 255 255 / 0.08);
  --qe-check-border: rgb(107 114 128);
  --qe-active: rgb(96 165 250);
  --qe-active-bg: rgb(59 130 246 / 0.2);
  --qe-text: rgb(229 231 235);
  --qe-muted: rgb(107 114 128);
  --qe-panel: rgb(28 33 40);
  --qe-code-bg: rgb(255 255 255 / 0.06);
  --qe-code-fg: rgb(229 231 235);
  --qe-quote-bar: rgb(75 85 99);
  --qe-shadow: 0 10px 24px -8px rgb(0 0 0 / 0.6);
}

/* ---------- toolbar shell ---------- */

/* One row, always. Wrapping into two or three rows made the toolbar read as
   a pile of loose icons rather than a single control strip. It stays one row
   by scrolling itself when the editor is genuinely too narrow to hold every
   group (per CLAUDE.md: wide content scrolls inside its own container, the
   page body never scrolls sideways). `scrollbar-width: none` hides the bar
   itself, since on a control strip it reads as damage. */
.qe-root .ql-toolbar {
  box-sizing: border-box;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border: 0;
  border-bottom: 1px solid var(--qe-divider);
  border-radius: 0.5rem 0.5rem 0 0;
  background: var(--qe-toolbar-bg);
  font-family: inherit;
}

.qe-root .ql-toolbar .ql-formats {
  display: inline-flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  align-items: center;
  gap: 1px;
  margin: 0;
  line-height: 0;
}

.qe-root .ql-toolbar::-webkit-scrollbar { display: none; }

/* Group separators. A pseudo element rather than extra markup, because the
   toolbar DOM belongs to Quill. It wraps with the group it belongs to, so a
   narrow toolbar never leaves an orphaned divider at the end of a row. */
.qe-root .ql-toolbar .ql-formats + .ql-formats::before {
  content: "";
  align-self: center;
  width: 1px;
  height: 18px;
  margin: 0 6px;
  background: var(--qe-separator);
  opacity: 0.7;
}

/* ---------- toolbar buttons ---------- */

.qe-root .ql-toolbar button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  float: none;
  width: 28px;
  height: 28px;
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.qe-root .ql-toolbar button svg {
  width: 18px;
  height: 18px;
  float: none;
}

.qe-root .ql-toolbar .ql-stroke,
.qe-root .ql-toolbar .ql-stroke-miter {
  stroke: var(--qe-icon);
}

.qe-root .ql-toolbar .ql-fill,
.qe-root .ql-toolbar .ql-stroke.ql-fill {
  fill: var(--qe-icon);
}

.qe-root .ql-toolbar .qe-thin {
  stroke-width: 1.6;
}

.qe-root .ql-toolbar button:hover,
.qe-root .ql-toolbar button:focus-visible {
  background: var(--qe-hover-bg);
}

.qe-root .ql-toolbar button:hover .ql-stroke,
.qe-root .ql-toolbar button:hover .ql-stroke-miter,
.qe-root .ql-toolbar button:focus-visible .ql-stroke,
.qe-root .ql-toolbar button:hover:not(.ql-active) .ql-stroke,
.qe-root .ql-toolbar button:hover:not(.ql-active) .ql-stroke-miter {
  stroke: var(--qe-icon-hover);
}

.qe-root .ql-toolbar button:hover .ql-fill,
.qe-root .ql-toolbar button:hover .ql-stroke.ql-fill,
.qe-root .ql-toolbar button:focus-visible .ql-fill,
.qe-root .ql-toolbar button:hover:not(.ql-active) .ql-fill,
.qe-root .ql-toolbar button:hover:not(.ql-active) .ql-stroke.ql-fill {
  fill: var(--qe-icon-hover);
}

.qe-root .ql-toolbar button.ql-active {
  background: var(--qe-active-bg);
}

.qe-root .ql-toolbar button.ql-active .ql-stroke,
.qe-root .ql-toolbar button.ql-active .ql-stroke-miter,
.qe-root .ql-toolbar button:hover.ql-active .ql-stroke,
.qe-root .ql-toolbar button:hover.ql-active .ql-stroke-miter {
  stroke: var(--qe-active);
}

.qe-root .ql-toolbar button.ql-active .ql-fill,
.qe-root .ql-toolbar button.ql-active .ql-stroke.ql-fill,
.qe-root .ql-toolbar button:hover.ql-active .ql-fill,
.qe-root .ql-toolbar button:hover.ql-active .ql-stroke.ql-fill {
  fill: var(--qe-active);
}

.qe-root .ql-toolbar button:focus {
  outline: none;
}

.qe-root .ql-toolbar button:focus-visible {
  outline: 2px solid var(--qe-active);
  outline-offset: -1px;
}

.qe-root .ql-toolbar button:disabled {
  cursor: default;
  opacity: 0.45;
}

.qe-root .ql-toolbar button.qe-busy {
  animation: qe-pulse 1.1s ease-in-out infinite;
}

@keyframes qe-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.8; }
}

@media (prefers-reduced-motion: reduce) {
  .qe-root .ql-toolbar button.qe-busy { animation: none; }
}

/* ---------- pickers (heading, alignment) ---------- */

.qe-root .ql-toolbar .ql-picker {
  height: 28px;
  color: var(--qe-icon);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
}

.qe-root .ql-toolbar .ql-picker-label {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 4px 0 8px;
  border: 0;
  border-radius: 6px;
  color: var(--qe-icon);
  transition: background-color 0.12s ease, color 0.12s ease;
}

.qe-root .ql-toolbar .ql-picker.ql-icon-picker .ql-picker-label {
  width: 28px;
  padding: 0;
  justify-content: center;
}

.qe-root .ql-toolbar .ql-picker-label:hover,
.qe-root .ql-toolbar .ql-picker.ql-expanded .ql-picker-label {
  background: var(--qe-hover-bg);
  color: var(--qe-icon-hover);
  border-color: transparent;
}

.qe-root .ql-toolbar .ql-picker.ql-expanded .ql-picker-label .ql-stroke {
  stroke: var(--qe-icon-hover);
}

.qe-root .ql-toolbar .ql-picker-label::before {
  line-height: 28px;
}

.qe-root .ql-toolbar .ql-picker-options {
  padding: 4px;
  border: 1px solid var(--qe-divider);
  border-radius: 10px;
  background: var(--qe-panel);
  box-shadow: var(--qe-shadow);
}

.qe-root .ql-toolbar .ql-picker.ql-expanded .ql-picker-options {
  border-color: var(--qe-divider);
  z-index: 30;
}

/* `.ql-formats` sets `line-height: 0` so the button row stays tight, and that
   inherits straight into the picker panels. Quill renders each heading option
   at its real size through `::before`, so with a zero line height those rows
   collapse to no height at all and print on top of one another. The panel has
   to opt back out. */
.qe-root .ql-toolbar .ql-picker-options {
  line-height: normal;
}

.qe-root .ql-toolbar .ql-picker-item {
  padding: 5px 10px;
  border-radius: 6px;
  color: var(--qe-text);
  line-height: 1.35;
}

/* Quill's own preview sizes, kept but reined in: 2em for H1 in a dropdown is
   larger than anything else in the UI and makes the panel unbalanced. */
.qe-root .ql-toolbar .ql-picker.ql-header .ql-picker-item[data-value="1"]::before { font-size: 1.55em; }
.qe-root .ql-toolbar .ql-picker.ql-header .ql-picker-item[data-value="2"]::before { font-size: 1.3em; }
.qe-root .ql-toolbar .ql-picker.ql-header .ql-picker-item[data-value="3"]::before { font-size: 1.15em; }
.qe-root .ql-toolbar .ql-picker.ql-header .ql-picker-item[data-value="4"]::before { font-size: 1.02em; }
.qe-root .ql-toolbar .ql-picker.ql-header .ql-picker-item[data-value="5"]::before { font-size: 0.94em; }

.qe-root .ql-toolbar .ql-picker-item:hover,
.qe-root .ql-toolbar .ql-picker-item.ql-selected {
  background: var(--qe-hover-bg);
  color: var(--qe-active);
}

.qe-root .ql-toolbar .ql-picker-item:hover .ql-stroke,
.qe-root .ql-toolbar .ql-picker-item.ql-selected .ql-stroke {
  stroke: var(--qe-active);
}

/* ---------- editing surface ---------- */

.qe-root .ql-container {
  position: relative;
  border: 0;
  border-radius: 0 0 0.5rem 0.5rem;
  font-family: inherit;
  font-size: 0.9375rem;
}

.qe-root .ql-editor {
  padding: 14px 16px;
  color: var(--qe-text);
  line-height: 1.65;
  overflow-wrap: break-word;
}

.qe-root .ql-editor.ql-blank::before {
  left: 16px;
  right: 16px;
  color: var(--qe-muted);
  font-style: normal;
}

.qe-root .ql-editor h1 { font-size: 1.5rem; font-weight: 700; }
.qe-root .ql-editor h2 { font-size: 1.25rem; font-weight: 700; }
.qe-root .ql-editor h3 { font-size: 1.0625rem; font-weight: 600; }
.qe-root .ql-editor h4,
.qe-root .ql-editor h5 { font-size: 0.9375rem; font-weight: 600; }

.qe-root .ql-editor a {
  color: var(--qe-active);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.qe-root .ql-editor blockquote {
  margin: 10px 0;
  padding-left: 14px;
  border-left: 3px solid var(--qe-quote-bar);
  color: var(--qe-text);
}

.qe-root .ql-editor code {
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--qe-code-bg);
  color: var(--qe-code-fg);
  font-size: 0.875em;
}

.qe-root .ql-editor .ql-code-block-container {
  margin: 10px 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--qe-code-bg);
  color: var(--qe-code-fg);
  font-size: 0.8125rem;
}

/* Never let an upload blow out the surrounding layout. */
.qe-root .ql-editor img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

/* ---------- checklists ---------- */

/* Snow draws checklist boxes as grey ☐ / ☑ text glyphs, which look like a
   font fallback. These are real boxes instead. They stay on the `.ql-ui` span
   because that span is what Quill listens to for ticking. It is absolutely
   positioned, so the strike through on a ticked line never reaches the box. */
.qe-root .ql-editor li[data-list=checked] > .ql-ui::before,
.qe-root .ql-editor li[data-list=unchecked] > .ql-ui::before {
  content: '';
  width: 1rem;
  height: 1rem;
  margin-right: 0.5em;
  vertical-align: -0.2em;
  border: 1.5px solid var(--qe-check-border);
  border-radius: 4px;
  background: var(--qe-panel) center / 0.75rem no-repeat;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.qe-root .ql-editor li[data-list=checked] > .ql-ui::before {
  border-color: var(--qe-active);
  background-color: var(--qe-active);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3.5 8.5l3 3 6-7'/%3E%3C/svg%3E");
}

.qe-root .ql-editor li[data-list=checked] {
  color: var(--qe-muted);
  text-decoration: line-through;
  text-decoration-color: var(--qe-muted);
}

.qe-root.qe-readonly .ql-container {
  border-radius: 0.5rem;
}

/* ---------- link tooltip ---------- */

/* Quill positions this tooltip from the caret and writes an inline `left`
   that regularly goes negative near the start of a line, which pushed the
   "Enter link" box out past the editor's left edge and under the page. The
   override needs `!important` precisely because Quill's value is inline.
   Pinning it to a fixed inset instead of tracking the caret is the trade:
   predictable and always fully visible beats "near the cursor, sometimes off
   screen". */
.qe-root .ql-snow .ql-tooltip {
  left: 12px !important;
  max-width: calc(100% - 24px);
  padding: 8px 10px;
  border: 1px solid var(--qe-divider);
  border-radius: 10px;
  background: var(--qe-panel);
  box-shadow: var(--qe-shadow);
  color: var(--qe-text);
  white-space: normal;
  z-index: 30;
}

.qe-root .ql-snow .ql-tooltip input[type="text"] {
  min-width: 0;
  padding: 5px 8px;
  border: 1px solid var(--qe-divider);
  border-radius: 6px;
  background: transparent;
  color: var(--qe-text);
  font-size: 0.8125rem;
}

.qe-root .ql-snow .ql-tooltip a.ql-action,
.qe-root .ql-snow .ql-tooltip a.ql-remove,
.qe-root .ql-snow .ql-tooltip a.ql-preview {
  color: var(--qe-active);
}

/* ------------------------------------------------------------------ *
 * Image resize overlay (see quillImageResize.js)
 * ------------------------------------------------------------------ */

/* Sits over the selected image inside .qe-root, which is `relative`. */
.qe-resizer {
  position: absolute;
  z-index: 5;
  box-sizing: border-box;
  border: 1.5px solid var(--qe-active);
  border-radius: 2px;
  /* The overlay must never eat clicks meant for the text under it. Only the
     handles themselves are interactive. */
  pointer-events: none;
}

.qe-resizer-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: #fff;
  border: 1.5px solid var(--qe-active);
  pointer-events: auto;
  transition: transform 0.12s ease, background-color 0.12s ease;
}

.qe-resizer-handle:hover {
  transform: scale(1.25);
  background: var(--qe-active);
}

.qe-resizer-label {
  position: absolute;
  bottom: 6px;
  right: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgb(15 23 42 / 0.78);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  pointer-events: none;
}

/* An image is a resize target, so say so before it is clicked. */
.qe-root .ql-editor img {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.qe-root .ql-editor img:hover {
  box-shadow: 0 0 0 2px var(--qe-active-bg);
}

/* Tables. Quill's core stylesheet gives them almost nothing, so without
   these a table renders as unaligned text with no visible grid. */
.qe-root .ql-editor table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
  table-layout: fixed;
}
.qe-root .ql-editor table td,
.qe-root .ql-editor table th {
  border: 1px solid var(--qe-divider);
  padding: 6px 10px;
  vertical-align: top;
  min-width: 40px;
}
.qe-root .ql-editor table th {
  background: var(--qe-toolbar-bg);
  font-weight: 600;
  text-align: left;
}
</style>
